import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto, SubmissionStatus } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { PLOFormOneData } from "../types/PLOs/plo_one.type";

const ENDPOINT = "plo-form-one";

export interface PLOFetchResponse {
  data: PLOFormOneData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for PLO Form One
// ---------------------------------------------------------------------------
export const mapPloBackendToFrontend = (item: any): PLOFormOneData => {
  if (!item) return {} as PLOFormOneData;

  // Extract nested form entity
  const form = item.ploFormOne || item.formData || item;

  return {
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    courtName: form.courtName || "",
    caseNumber: form.caseNumber || "",
    advocateOfficeName: form.advocateOfficeName || "",
    plaintiffOrAccused: form.plaintiffOrAccused || "",
    policeStationInfoAndNumber: form.policeStationInfoAndNumber || "",
    policeReportDate: form.policeReportDate || "",
    caseSummary: form.caseSummary || "",
    chargeSection: form.chargeSection || "",
    chargeSectionDate: form.chargeSectionDate || "",
    witnessDetails: form.witnessDetails || "",
    interviewerDetails: form.interviewerDetails || "",
    interviewDate: form.interviewDate || "",
    interviewLocation: form.interviewLocation || "",
    supportingOrganization: form.supportingOrganization || "",
    observerName: form.observerName || "",
    courtSummonsDate: form.courtSummonsDate || "",
    finalOrderDetails: form.finalOrderDetails || "",
    finalOrderDate: form.finalOrderDate || "",

    // Signatures & Status Metadata
    signName: item.signerName || form.signName || form.signerName || "",
    signerName: item.signerName || form.signerName || form.signName || "",
    signatureImage: formatSignatureUrl(
      item.signatureImage || form.signatureImage
    ),
    status: (item.status || form.status || "DRAFT") as SubmissionStatus,

    // Geo Levels (FIXED: Added targetGeoLevels and originGeoLevel)
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
  ? item.stageHistory.map((history: any) => ({
      id: history.id,
      action: history.action,
      comments: history.comments,
      attachments: history.attachments,
      createdAt: history.createdAt,
      performedBy: history.performedBy
        ? {
            id: history.performedBy.id,
            name: history.performedBy.name,
            role: history.performedBy.role,
            position: history.performedBy.position,
            office: history.performedBy.office
              ? {
                  id: history.performedBy.office.id,
                  name: history.performedBy.office.name,
                  code: history.performedBy.office.code,
                  level: history.performedBy.office.level,
                }
              : undefined,
          }
        : undefined,
    }))
  : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for PLO Form One
// ---------------------------------------------------------------------------
const ploFilterPredicate = (
  app: PLOFormOneData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate ||
    Boolean(
      app.policeReportDate && app.policeReportDate.startsWith(selectedDate)
    );

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.caseNumber && app.caseNumber.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(
      app.plaintiffOrAccused &&
        app.plaintiffOrAccused.toLowerCase().includes(term)
    ) ||
    Boolean(
      app.chargeSection && app.chargeSection.toLowerCase().includes(term)
    ) ||
    Boolean(
      app.documentNumber && app.documentNumber.toLowerCase().includes(term)
    );

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions (Office User - Form Endpoint)
// ---------------------------------------------------------------------------

// Fetch All / Filtered Applications
export const fetchPloApplications = (
  params: FetchParams
): Promise<PLOFetchResponse> =>
  genericService.fetchApplications<PLOFormOneData>(
    ENDPOINT,
    params,
    mapPloBackendToFrontend,
    ploFilterPredicate
  );

// Fetch Single Application by ID
export const fetchPloById = (id: string): Promise<PLOFormOneData> =>
  genericService.fetchApplicationById<PLOFormOneData>(
    ENDPOINT,
    id,
    mapPloBackendToFrontend
  );

// Save Draft (Create POST or Update PUT)
export const savePloDraft = (
  id: string | null,
  data: Partial<PLOFormOneData>
) => genericService.saveDraft<PLOFormOneData>(ENDPOINT, id, data);

// Submit Form (Includes Signatures inside DTO)
export const submitPloForm = (id: string, data: PLOFormOneData) =>
  genericService.submitForm<PLOFormOneData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deletePloForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions (Global DocumentController)
// ---------------------------------------------------------------------------

export const startReviewPloForm = (id: string) =>
  genericService.startReviewForm(id);

// Approve Form (PATCH /documents/:id/approve)
// export const approvePloForm = (id: string, remark?: string) =>
//   genericService.attachForm(id, remark);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);

export const attachPloForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);



// // Return Form for Revisions (PATCH /documents/:id/return)
// export const returnPloForm = (id: string, remark?: string) =>
//   genericService.returnForm(id, remark);

// // Reject Form (PATCH /documents/:id/reject)
// export const rejectPloForm = (id: string, remark?: string) =>
//   genericService.rejectForm(id, remark);